import React from 'react'
import PropTypes from 'prop-types'
import { ButtonGroup, withStyles } from '@material-ui/core'
import clsx from 'clsx'

const styles = theme => ({
  root: {},
  button: {},
  active: {
    color: theme.palette.primary.main,
    background: theme.palette.primary.highlight,
    borderColor: theme.palette.primary.main,
    // override the border color for active buttons that are not disabled
    '&:not(.Mui-disabled)': {
      borderRightColor: theme.palette.primary.main + '!important'
    }
  }
})

/**
 * Wrapper component for buttons. Children should be buttons,
 * and will receive an "active" class if the corresponding value or index
 * is provided in the `active` prop.
 */
const HypButtonGroup = React.forwardRef(
  (
    {
      classes,
      className,
      active,
      children,
      toggleAll,
      onChange,
      ...props
    },
    ref
  ) => {
    // returns true if the child button is active
    const isChildActive = (child, index) => {
      const value = child?.props?.value || index
      if (!toggleAll) return active === value
      return active.indexOf(value) !== -1
    }

    const handleChange = (child, index) => {
      const value = child?.props?.value || index
      if (!toggleAll) {
        const newValue = active === value ? null : value
        return onChange && onChange(newValue)
      }
      const newActive =
        active.indexOf(value) > -1 && value !== 'all'
          ? active.filter(item => item !== value)
          : [...active, value]
      return onChange && onChange(newActive)
    }

    const childrenWithProps = React.Children.map(
      children,
      (child, index) => {
        //rm'd value, label
        const { ...props } = child.props
        return React.cloneElement(child, {
          ...props,
          className: clsx(
            classes.button,
            {
              [classes.active]: isChildActive(child, index)
            },
            child.className
          ),
          onClick: () => handleChange(child, index)
        })
      }
    )

    return (
      <ButtonGroup
        ref={ref}
        variant="outlined"
        className={clsx(classes.root, className)}
        {...props}>
        {childrenWithProps}
      </ButtonGroup>
    )
  }
)

HypButtonGroup.displayName = 'HypButtonGroup'
HypButtonGroup.propTypes = {
  /** Determines if all button can be toggled on / off, or if only one button can be active at a time */
  toggleAll: PropTypes.bool,
  /** a signle string value or an array of active button values or indexes to dictate which buttons are active */
  active: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array
  ]),
  /** Handler function for when a button is clicked */
  onChange: PropTypes.func,
  /** children should be <Button> or <button> with a `value` prop */
  children: PropTypes.node,
  /** Class name for the root */
  className: PropTypes.string,
  /** An object of class names applied to the root, button, and active elements */
  classes: PropTypes.object
}

export default withStyles(styles)(HypButtonGroup)
